import { OptionChain, StandardDeviation } from '.';
import { SentimentType, WhatIfDefaultPriceType } from '../enums/enums';
import ApplicationContext from './application-context';
import Combination from './combination';
import DateTimeHelper from './date-time-helper';
import formatting from './formatting';
import HowDataModel from './how-data-model';
import NumberFormatHelper from './number-format-helper';
import { returnBasisFor } from './return-basis';
import { TradingRangeSimulator } from './trading-range-simulator';

export interface IWhatifTheoretical {
  payoffValue: number;
  payoffFormatted: string;
  returnValue: number;
  returnPercentageFormatted: string;
}

export interface IWhatIfDefaultInput {
  ticks: number[]; // sdSliderTicks: prices at [-2, -1, 0, +1, +2] SD (index 2 = distribution mean)
  last: number;
  sentiment?: SentimentType;
  accountPriceType?: string; // userSettings.whatIfDefaultPriceType
  categoryPriceType?: string; // category.whatIf.priceType
  categoryPriceSd?: number; // category.whatIf.priceSd
}

/**
 * WP3 — pure precedence for the P&L Simulator's opening price:
 * 1. category price type, else account's, else Sd.
 * 2. if Current price → the last price.
 * 3. if Sd → the category's SD, else the Outlook's SD.
 */
export const defaultWhatIfPrice = (i: IWhatIfDefaultInput): number => {
  const priceType = (i.categoryPriceType || i.accountPriceType || WhatIfDefaultPriceType.SD).toUpperCase();
  if (priceType !== WhatIfDefaultPriceType.SD.toUpperCase()) {
    return NumberFormatHelper.roundNumber(i.last);
  }
  const sentimentSd = i.sentiment === SentimentType.BULLISH ? 1 : i.sentiment === SentimentType.BEARISH ? -1 : 0;
  const sd = Math.max(-2, Math.min(2, i.categoryPriceSd ?? sentimentSd));
  const tick = i.ticks[2 + sd];
  return NumberFormatHelper.roundNumber(tick !== undefined ? tick : i.last * (1 + 0.15 * sd));
};

class WhatIfSimulator {
  whatIfSPrice = 0;
  whatIfDate: Date | undefined = DateTimeHelper.getCurrentDate();
  whatIfVolatility = 0.3;
  sentiment: SentimentType | undefined;
  chain: OptionChain | undefined;
  stdDev: StandardDeviation | undefined;
  hasOption: boolean = false;
  last: number = 0;
  dateOfBellSlider: Date | undefined;
  sdSliderTicks: number[] = [];
  // WP3 — per-category what-if defaults; undefined when the category has none.
  defaultPriceType: string | undefined;
  defaultPriceSd: number | undefined;

  private constructor() { }

  static fromData = (
    howDataModel: HowDataModel,
    sentiment: SentimentType,
    tradingRangeSimulator: TradingRangeSimulator,
    whatIfDefaults?: { priceType?: string; priceSd?: number },
  ) => {
    const model = new WhatIfSimulator();
    model.hasOption = howDataModel.hasOption;
    model.last = howDataModel.quote.last;
    model.chain = howDataModel.chain;
    model.stdDev = howDataModel.stdDev;
    model.sentiment = sentiment;
    model.dateOfBellSlider = tradingRangeSimulator.dateOfBellSlider;
    model.whatIfDate = tradingRangeSimulator.dateOfBellSlider;
    model.sdSliderTicks = tradingRangeSimulator.sds;
    model.defaultPriceType = whatIfDefaults?.priceType;
    model.defaultPriceSd = whatIfDefaults?.priceSd;
    model.resetPriceRange();
    model.resetWhatIfVolatility();
    return model;
  };

  public static fromSelf = (self: WhatIfSimulator) => {
    const clone = new WhatIfSimulator();
    clone.whatIfSPrice = self.whatIfSPrice;
    clone.sentiment = self.sentiment;
    clone.hasOption = self.hasOption;
    clone.last = self.last;
    clone.chain = self.chain;
    clone.stdDev = self.stdDev;
    clone.whatIfDate = self.whatIfDate;
    clone.whatIfVolatility = self.whatIfVolatility;
    clone.dateOfBellSlider = self.dateOfBellSlider;
    // WP3 — carry category defaults so Reset returns to the category default, not the sentiment default.
    clone.defaultPriceType = self.defaultPriceType;
    clone.defaultPriceSd = self.defaultPriceSd;
    return clone;
  };

  get whatIfDays() {
    return formatting.daysFromNow(this.whatIfDate);
  }

  set whatIfDays(newDays: number) {
    let date = formatting.getDateFromDaysInFuture(newDays);
    this.whatIfDate = date;
  }

  minVolatility = () => {
    if (this.chain) {
      let dateOfBell = this.dateOfBellSlider;
      let result = this.chain.impliedVolatility(dateOfBell) * 0.5;
      if (result > 0) {
        return result;
      }
    }
    return 0;
  };

  maxVolatility = () => {
    if (this.chain) {
      let dateOfBell = this.dateOfBellSlider;
      let result = this.chain.impliedVolatility(dateOfBell) * 1.5;
      return result;
    }
    return 100;
  };

  //TODO: revisit, i guess achor should be set by this value. Presently its doing 50%
  whatIfVolatilityAnchor = () => {
    let volatilityAnchor = 30;
    if (this.chain && this.stdDev) {
      volatilityAnchor = this.chain.impliedVolatility(this.dateOfBellSlider);
    }
    return volatilityAnchor;
  };

  resetPriceRange = () => {
    this.whatIfSPrice = defaultWhatIfPrice({
      ticks: this.sdSliderTicks,
      last: this.last,
      sentiment: this.sentiment,
      accountPriceType: ApplicationContext.configuration.userSettings?.whatIfDefaultPriceType,
      categoryPriceType: this.defaultPriceType,
      categoryPriceSd: this.defaultPriceSd,
    });
  };

  resetWhatIfDate = () => {
    this.whatIfDate = this.dateOfBellSlider;
  };

  resetWhatIfVolatility = () => {
    this.whatIfVolatility = this.whatIfVolatilityAnchor();
  };

  whatifTheoretical = (comb: Combination): IWhatifTheoretical => {
    /* this is the calculation of the payoff price
    * based on the what id price and what if date
    * this is what we will need to change for the index
    * but for this we need the current price at the time
    * that this calculation is done
    * so... how do we get the current price?
    * voila... this.price has the current price
    
    * TODO: This is where the adjustments needs to be done
    * TODO: Before the values are passed to the payoff method
    * variables that we'll need: this.price, this.chain
    * find the nearest expiry > whatIfDate
    * find the date of expiration for the leg being evaluated
    * whatIfPrice = whatIfPrice
    */
    /**
     * As per the CR In portfolio for return "payoff should be divided with costbasis".
     * Mark should not be considered as Mark by def is a comb.price().
     * Costbasis is what we invested/bought positions.
     */
    // TODO: Instead of portfolio check position.isowned
    const po = comb.payoff(this.whatIfSPrice, this.whatIfDate, this.whatIfVolatility / 100);
    const basis = returnBasisFor(comb, comb.isPortfolio, comb.isPortfolio ? comb.quote.last : this.last);
    const pc = basis !== 0 ? po / basis : 0;
    return {
      payoffValue: po,
      payoffFormatted: NumberFormatHelper.toCurrency(po),
      returnValue: pc,
      returnPercentageFormatted: NumberFormatHelper.toPercentage(pc * 100, 'auto'), // signed: "-100.00%"
    };
  };
}

export default WhatIfSimulator;
